import React, {useState, useEffect} from "react";
import {Skeleton} from "primereact/skeleton";
import axios from "axios";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {FloatLabel} from "primereact/floatlabel";
import {InputText} from "primereact/inputtext";
import {AutoComplete} from "primereact/autocomplete";
import {
	base,
	get_contract_instances,
	partners,
	volunteers,
	unit,
	position_and_service,
	workplace_url,
	budget_by_project,
} from "../../../api";
import DisabledFormFields from "../widgets/DisabledFormFields";
import DocumentTypeSelector from "../widgets/DocumentTypeSelector";
import AutoAddRemoveRows from "../widgets/AutoAddRemoveRows";

// Normalize date values to YYYY-MM-DD
const normalizeValue = val => {
	if (val instanceof Date) {
		return val.toISOString().split("T")[0];
	}
	if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}/.test(val)) {
		return val;
	}
	return val;
};

export default function ProcessAddDialogForm({
	isDialogVisible,
	setIsDialogVisible,
	data,
	selectedTemplatePage,
	fetchContractData,
	newContractTitle,
	setNewContractTitle,
	toast,
	base: apiBase,
	projects_url,
}) {
	const [selectedProject, setSelectedProject] = useState(null);
	const [selectedPartner, setSelectedPartner] = useState(null);
	const [formRelease, setFormRelease] = useState(false);
	const [projects, setProjects] = useState([]);
	const [filteredProjects, setFilteredProjects] = useState([]);
	const [filteredPartners, setFilteredPartners] = useState([]);
	const [partnersList, setPartnersList] = useState([]);
	const [partnersLoading, setPartnersLoading] = useState(true);

	const [volunteersList, setVolunteersList] = useState([]);
	const [filteredVolunteers, setFilteredVolunteers] = useState([]);
	const [volunteersLoading, setVolunteersLoading] = useState(true);

	const [unitsList, setUnitsList] = useState([]);
	const [filteredUnits, setFilteredUnits] = useState([]);
	const [unitsLoading, setUnitsLoading] = useState(true);
	// load all partners on mount (paginated fetch)

	const [positionsList, setPositionsList] = useState([]);
	const [filteredPositions, setFilteredPositions] = useState([]);
	const [positionsLoading, setPositionsLoading] = useState(true);

	// Work Places
	// Budget error state
	const [budgetError, setBudgetError] = useState("");
	const [workPlacesList, setWorkPlacesList] = useState([]);
	const [filteredWorkPlaces, setFilteredWorkPlaces] = useState([]);
	// Search for work places
	const searchWorkPlace = e => {
		const q = typeof e.query === "string" ? e.query.toLowerCase() : "";
		const results = workPlacesList.filter(
			w =>
				String(w.title_am || "")
					.toLowerCase()
					.includes(q) ||
				String(w.title_en || "")
					.toLowerCase()
					.includes(q),
		);
		setFilteredWorkPlaces(results);
	};
	// console.log({selectedPartner});

	const fetchAllPartners = async () => {
		try {
			const res = await axios.get(base + partners);
			setPartnersList(res.data);
			setFilteredPartners(res.data);
		} catch {
			setPartnersList([]);
			setFilteredPartners([]);
		}
		setPartnersLoading(false);
	};

	const loadProjects = async () => {
		try {
			const res = await axios.get(apiBase + projects_url);
			setProjects(res.data);
		} catch {}
	};
	// console.log(partnersList);
	const searchPartner = e => {
		const q = e.query.toLowerCase();
		const results = partnersList.filter(
			p =>
				p.full_name_am?.toLowerCase().includes(q) ||
				p.full_name_en?.toLowerCase().includes(q) ||
				p.legal_full_name_am?.toLowerCase().includes(q) ||
				p.legal_full_name_en?.toLowerCase().includes(q) ||
				p.legal_head_am?.toLowerCase().includes(q) ||
				p.legal_head_en?.toLowerCase().includes(q) ||
				String(p.id).includes(q),
		);
		setFilteredPartners(results);
	};

	const searchVolunteers = e => {
		const q = typeof e.query === "string" ? e.query.toLowerCase() : "";
		const results = volunteersList.filter(v => (v.full_name || "").toLowerCase().includes(q));
		setFilteredVolunteers(results);
	};

	const searchUnits = e => {
		const q = typeof e.query === "string" ? e.query.toLowerCase() : "";
		const results = unitsList.filter(
			u =>
				String(u.title_am || "")
					.toLowerCase()
					.includes(q) ||
				String(u.title_en || "")
					.toLowerCase()
					.includes(q),
		);
		setFilteredUnits(results);
	};
	const searchPositions = e => {
		const q = typeof e.query === "string" ? e.query.toLowerCase() : "";
		const results = positionsList.filter(
			p =>
				String(p.title_am || "")
					.toLowerCase()
					.includes(q) ||
				String(p.title_en || "")
					.toLowerCase()
					.includes(q),
		);
		setFilteredPositions(results);
	};
	const [selectedID, setSelectedID] = useState(null);
	const [selectedKeys, setSelectedKeys] = useState([]); // { field_name, description, type }[]
	const [availableKeys, setAvailableKeys] = useState([]);
	const [inputValues, setInputValues] = useState({});
	const [processName, setProcessName] = useState("");
	const [selectedTypes, setSelectedTypes] = useState([]);
	const [cleanedFileNames, setCleanedFileNames] = useState([]);

	const [rows, setRows] = useState([
		{description: "", unit: null, price: "", count: "", expense_type: null},
	]);
	const [rowsError, setRowsError] = useState(false);
	const [disabledBtn, setDisabledBtn] = useState(true);

	// when dialog opens or template/page changes
	useEffect(() => {
		if (!isDialogVisible || !data || !selectedTemplatePage) return;

		fetchAllPartners();
		loadProjects();

		axios
			.get(base + volunteers)
			.then(res => {
				setVolunteersList(res.data);
				setFilteredVolunteers(res.data);
				setVolunteersLoading(false);
			})
			.catch(() => {
				setVolunteersList([]);
				setFilteredVolunteers([]);
				setVolunteersLoading(false);
			});

		axios
			.get(base + unit)
			.then(res => {
				setUnitsList(res.data);
				setFilteredUnits(res.data);
				setUnitsLoading(false);
			})
			.catch(() => {
				setUnitsList([]);
				setFilteredUnits([]);
				setUnitsLoading(false);
			});

		axios
			.get(base + position_and_service)
			.then(res => {
				setPositionsList(res.data);
				setFilteredPositions(res.data);
				setPositionsLoading(false);
			})
			.catch(() => {
				setPositionsList([]);
				setFilteredPositions([]);
				setPositionsLoading(false);
			});

		// fetch work places
		axios
			.get(base + workplace_url)
			.then(res => {
				setWorkPlacesList(res.data);
				setFilteredWorkPlaces(res.data);
			})
			.catch(() => {
				setWorkPlacesList([]);
				setFilteredWorkPlaces([]);
			});

		// find existing contract instance id
		const match = data.find(d => d.contract_type?.id === selectedTemplatePage.id);
		setSelectedID(match?.id ?? null);

		// parse form_fields JSON
		let formFields = [];
		if (selectedTemplatePage.form_fields) {
			try {
				formFields = JSON.parse(selectedTemplatePage.form_fields);
			} catch {}
		}

		// selected keys = those fields
		const initialSelected = formFields.map(f => ({
			field_name: f.field_name,
			description: f.description || "",
			type: f.type || "",
		}));
		setSelectedKeys(initialSelected);

		// availableKeys = remaining fields (if editing, you'd merge logic; here simple)
		setAvailableKeys(
			formFields.filter(f => !initialSelected.some(s => s.field_name === f.field_name)),
		);
		// clean file names
		const files = selectedTemplatePage.file_templates || [];
		const cleaned = files
			.map(file => {
				const name = file.file_name?.split("/").pop() || "";
				const prefix = name.match(/^[^_]+_/)?.[0] || "";
				return {
					...file,
					cleanedFileName: name.replace(prefix, ""),
				};
			})
			.filter(f => f.cleanedFileName);
		setCleanedFileNames(cleaned);
	}, [isDialogVisible, data, selectedTemplatePage]);

	// fetch budget_by_project when selectedProject changes
	useEffect(() => {
		if (!selectedProject?.id) return;

		axios
			.get(`${base + budget_by_project}/${selectedProject.id}`)
			.then(res => {
				console.log("budget_by_project:", res.data);
				setBudgetError("");
			})
			.catch(err => {
				const detail = err?.response?.data?.detail;
				if (detail === "Not found.") {
					setBudgetError("Նախագիծը չունի բյուջե");
				} else {
					setBudgetError("");
				}
				console.error("Failed to fetch budget_by_project", err);
			});
	}, [selectedProject]);

	// enable Save button when all required filled
	useEffect(() => {
		const rowsValid = rows.every(
			r => r.description && r.unit && r.price && r.count && r.expense_type,
		);
		const inputsValid = selectedKeys.every(k => {
			const v = inputValues[k.field_name];
			return v !== null && v !== undefined && String(v).trim() !== "";
		});
		if (processName && rowsValid && selectedTypes.length > 0 && inputsValid) {
			setDisabledBtn(false);
		} else {
			setDisabledBtn(true);
		}
	}, [processName, rows, selectedTypes, inputValues, selectedKeys]);

	const handleInputChange = (field, value) => {
		setInputValues(prev => ({...prev, [field]: value}));
	};

	const handleRemoveField = key => {
		setSelectedKeys(sk => sk.filter(s => s.field_name !== key.field_name));
		setInputValues(prev => {
			const nv = {...prev};
			delete nv[key.field_name];
			return nv;
		});
		setAvailableKeys(ak => {
			if (!ak.some(k => k.field_name === key.field_name)) {
				return [...ak, key];
			}
			return ak;
		});
	};

	const validateRows = () => rows.every(r => r.description && r.count && r.price && r.expense_type);

	const saveData = async () => {
		if (!validateRows()) {
			setRowsError(true);
			return;
		}
		const payloadInstance = {
			name: processName,
			enabled_template_types: selectedTypes.map(t => t.id),
			contract_type: selectedTemplatePage.id,
			partner: selectedPartner?.id,
			project_name:
				inputValues.project_name && typeof inputValues.project_name === "object"
					? inputValues.project_name.id
					: typeof inputValues.project_name === "number"
					? inputValues.project_name
					: normalizeValue(inputValues.project_name ?? ""),
			project: selectedProject?.id,
			contract_location:
				selectedKeys.some(k => k.field_name === "contract_location") &&
				inputValues.contract_location &&
				typeof inputValues.contract_location === "object"
					? inputValues.contract_location.id
					: typeof inputValues.contract_location === "number"
					? inputValues.contract_location
					: normalizeValue(inputValues.contract_location ?? ""),
			employee_project: normalizeValue(inputValues.employee_project ?? ""),
			location_of_additional_agreement: normalizeValue(
				inputValues.location_of_additional_agreement ?? "",
			),
			order_location: normalizeValue(inputValues.order_location ?? ""),
			partner_organization: normalizeValue(inputValues.partner_organization ?? ""),
			volunteer: normalizeValue(inputValues.volunteer ?? ""),
			work_place: normalizeValue(inputValues.work_place ?? ""),
			form_fields: selectedKeys.map(k => {
				const val = inputValues[k.field_name];
				return {
					...k,
					value: val && typeof val === "object" && "id" in val ? val.id : normalizeValue(val ?? ""),
				};
			}),
			...Object.fromEntries(
				selectedKeys.map(k => [
					k.field_name,
					inputValues[k.field_name] &&
					typeof inputValues[k.field_name] === "object" &&
					"id" in inputValues[k.field_name]
						? inputValues[k.field_name].id
						: normalizeValue(inputValues[k.field_name] ?? ""),
				]),
			),
		};
		if (
			inputValues.volunteer_name &&
			typeof inputValues.volunteer_name === "object" &&
			"id" in inputValues.volunteer_name
		) {
			payloadInstance.volunteer_name = inputValues.volunteer_name.id;
		}
		if (
			inputValues.position_and_service &&
			typeof inputValues.position_and_service === "object" &&
			"id" in inputValues.position_and_service
		) {
			payloadInstance.position_and_service = inputValues.position_and_service.id;
		}
		if (
			inputValues.unit_of_measurement &&
			typeof inputValues.unit_of_measurement === "object" &&
			"id" in inputValues.unit_of_measurement
		) {
			payloadInstance.unit_of_measurement = inputValues.unit_of_measurement.id;
		}
		const payloadProducts = rows.map(r => ({
			product: r.expense_type?.id || r.expense_type,
			price: r.price,
			unit: r.unit?.id || r.unit,
			count: r.count,
			description: r.description,
		}));
		try {
			const inst = await axios.post(apiBase + get_contract_instances, payloadInstance, {
				headers: {"Content-Type": "application/json"},
			});
			const cid = inst.data.id;
			await axios.post(`${apiBase + get_contract_instances + cid}/bulk-upload/`, payloadProducts, {
				headers: {"Content-Type": "application/json"},
			});
			toast.current.show({
				severity: "success",
				summary: "Success",
				detail: "Data saved successfully",
			});
			setIsDialogVisible(false);
			fetchContractData();
			clearForm();
		} catch (err) {
			toast.current.show({severity: "error", summary: "Error", detail: "Failed to save data"});
		}
	};
	// console.log(filteredPartners);

	const clearForm = () => {
		setSelectedProject(null);
		setFormRelease(false);
		setSelectedKeys([]);
		setAvailableKeys([]);
		setInputValues({});
		setProcessName("");
		setSelectedTypes([]);
		setCleanedFileNames([]);
		setRows([{description: "", unit: null, price: "", count: "", expense_type: null}]);
		setRowsError(false);
		setDisabledBtn(true);
	};

	// control formRelease by project selection
	useEffect(() => {
		if (!selectedProject) return setFormRelease(false);
		const valid = filteredProjects.some(p => p.id === selectedProject.id);
		setFormRelease(valid);
	}, [selectedProject, filteredProjects]);

	const searchProject = e => {
		const q = e.query.toLowerCase();
		setFilteredProjects(
			projects.filter(
				p =>
					p.full_name_am.toLowerCase().includes(q) ||
					(p.name && p.name.toLowerCase().includes(q)) ||
					String(p.id).includes(q),
			),
		);
	};

	// scroll to top on open
	useEffect(() => {
		if (isDialogVisible) {
			const c = document.querySelector(".p-dialog .p-dialog-content");
			if (c) c.scrollTop = 0;
		}
	}, [isDialogVisible]);

	const footer = formRelease && (
		<div className="pt-3">
			<Button
				label="Cancel"
				icon="pi pi-times"
				severity="secondary"
				rounded
				onClick={() => {
					clearForm();
					setIsDialogVisible(false);
				}}
			/>
			<Button
				label="Save"
				icon="pi pi-check"
				severity="primary"
				rounded
				// disabled={disabledBtn}
				onClick={saveData}
				autoFocus
			/>
		</div>
	);

	return (
		<Dialog
			visible={isDialogVisible}
			onHide={() => {
				clearForm();
				setSelectedPartner(null);
				setSelectedProject(null);
				setSelectedID(null);
				setSelectedKeys([]);
				setAvailableKeys([]);
				setInputValues({});
				setProcessName("");
				setSelectedTypes([]);
				setCleanedFileNames([]);
				setRows([{description: "", unit: null, price: "", count: "", expense_type: null}]);
				setRowsError(false);
				setDisabledBtn(true);
				setFormRelease(false);
				setFilteredProjects([]);
				setFilteredPartners([]);
				setIsDialogVisible(false);
			}}
			header={`${selectedTemplatePage?.name || ""} - ${newContractTitle}`}
			modal
			footer={footer}
			className="col-lg-8 col-11 mx-auto">
			<div className="row">
				<div className="col-12 mb-3">
					<label htmlFor="project">Select Project</label>
					<AutoComplete
						id="project"
						value={selectedProject}
						suggestions={filteredProjects}
						completeMethod={searchProject}
						field="full_name_am"
						onChange={e => setSelectedProject(e.value)}
						placeholder="Search by name or ID"
						dropdown
						className="w-100"
						panelStyle={{width: "300px"}}
						itemTemplate={item => (
							<div>
								<div className="text-dark">{item.full_name_am}</div>
								<small className="text-primary">
									{item.name || "No code"} (ID: {item.id})
								</small>
							</div>
						)}
					/>
				</div>

				{formRelease && (
					<>
						{budgetError && (
							<div className="col-12 text-danger pb-3" style={{fontWeight: "bold"}}>
								{budgetError}
							</div>
						)}
						<div className="col-12 mb-3">
							<FloatLabel>
								<InputText
									id="processName"
									value={processName}
									onChange={e => {
										setProcessName(e.target.value);
										setNewContractTitle(e.target.value);
									}}
									className="w-100"
								/>
								<label htmlFor="processName">Contract Title</label>
							</FloatLabel>
						</div>

						<div className="col-12 mb-3">
							<div className="card flex justify-content-center">
								{partnersLoading ? (
									<Skeleton width="100%" height="45px" />
								) : (
									<AutoComplete
										dropdown
										placeholder="Մուտքագրեք օգտատեր"
										field="full_name_am"
										completeMethod={searchPartner}
										suggestions={filteredPartners}
										value={selectedPartner}
										onChange={e => {
											setSelectedPartner(e.value);
											if (e.value?.type_name !== "Physical" && e.value?.type_name !== "Legal") {
												handleInputChange("partner_organization_name", e.value?.full_name_am);
											} else {
												handleInputChange("partner_organization_name", null);
											}
										}}
										itemTemplate={item => (
											<div className="d-flex align-items-top">
												<div className="d-grid">
													<span className="text-primary">{item?.full_name_am}</span>
													<span className="text-secondary small">
														{item?.address_en ?? "not provided"}
													</span>
													<span className="text-secondary small">
														{item?.passport ?? "not provided"}
													</span>
												</div>
											</div>
										)}
										className="w-100"
									/>
								)}
							</div>
						</div>

						<div className="card b-radius b-shadow p-3 col-12 mb-3">
							<div className="row">
								{selectedKeys.length > 0 ? (
									selectedKeys.map(key => {
										if (key.field_name === "volunteer_name") {
											return (
												<div key={key.field_name} className="col-6 d-flex align-items-center">
													<div className="col-11 mb-2">
														<label className="ps-2 mb-2" htmlFor={key.field_name}>
															{key.description}
														</label>
														{volunteersLoading ? (
															<Skeleton width="100%" height="45px" />
														) : (
															<AutoComplete
																dropdown
																placeholder="Մուտքագրեք կամավոր"
																field="full_name"
																completeMethod={searchVolunteers}
																suggestions={filteredVolunteers}
																value={inputValues[key.field_name] || null}
																onChange={e => handleInputChange(key.field_name, e.value)}
																itemTemplate={vol => (
																	<div className="d-grid p-1" style={{width: "300px"}}>
																		<span className="text-primary">{vol?.full_name}</span>
																	</div>
																)}
																className="w-100 p-0"
															/>
														)}
													</div>
												</div>
											);
										}
										if (key.field_name === "unit_of_measurement") {
											return (
												<div key={key.field_name} className="col-6 d-flex align-items-center">
													<div className="col-11 mb-2">
														<label className="ps-2 mb-2" htmlFor={key.field_name}>
															{key.description}
														</label>
														{unitsLoading ? (
															<Skeleton width="100%" height="45px" />
														) : (
															<AutoComplete
																dropdown
																dropdownMode="current"
																placeholder="Մուտքագրեք միավոր"
																field="title_am"
																completeMethod={searchUnits}
																suggestions={filteredUnits}
																value={inputValues[key.field_name] || null}
																onChange={e => handleInputChange(key.field_name, e.value)}
																itemTemplate={unit => (
																	<div className="d-grid p-1" style={{width: "300px"}}>
																		<span className="text-primary">{unit?.title_am}</span>
																		<span className="text-secondary small">
																			{unit?.title_en || "not provided"}
																		</span>
																	</div>
																)}
																className="w-100 p-0"
															/>
														)}
													</div>
												</div>
											);
										}
										if (key.field_name === "position") {
											return (
												<div key={key.field_name} className="col-6 d-flex align-items-center">
													<div className="col-11 mb-2">
														<label className="ps-2 mb-2">{key.description}</label>
														{positionsLoading ? (
															<Skeleton width="100%" height="45px" />
														) : (
															<AutoComplete
																dropdown
																placeholder="Մուտքագրեք պաշտոն կամ ծառայություն"
																field="title_am"
																completeMethod={searchPositions}
																suggestions={filteredPositions}
																value={inputValues[key.field_name] || null}
																onChange={e => handleInputChange(key.field_name, e.value)}
																itemTemplate={pos => (
																	<div className="d-grid p-1" style={{width: "300px"}}>
																		<span className="text-primary">{pos?.title_am}</span>
																		<span className="text-secondary small">
																			{pos?.title_en || "not provided"}
																		</span>
																	</div>
																)}
																className="w-100 p-0"
															/>
														)}
													</div>
												</div>
											);
										}
										// Add partner_name special handling
										if (key.field_name === "partner_name") {
											return (
												<div key={key.field_name} className="col-6 d-flex align-items-center">
													<div className="col-11 mb-2">
														<label className="ps-2 mb-2" htmlFor={key.field_name}>
															{key.description}
														</label>
														{partnersLoading ? (
															<Skeleton width="100%" height="45px" />
														) : (
															<AutoComplete
																dropdown
																placeholder="Մուտքագրեք օգտատեր"
																field="full_name_am"
																completeMethod={searchPartner}
																suggestions={filteredPartners}
																value={selectedPartner}
																onChange={e => {
																	setSelectedPartner(e.value);
																	handleInputChange(key.field_name, e.value);
																	handleInputChange(
																		"partner_organization_name",
																		e.value?.type_name !== "Physical" &&
																			e.value?.type_name !== "Legal"
																			? e.value?.full_name_am
																			: null,
																	);
																}}
																itemTemplate={item => (
																	<div className="d-flex align-items-top">
																		<div className="d-grid">
																			<span className="text-primary">{item?.full_name_am}</span>
																			<span className="text-secondary small">
																				{item?.address_en ?? "not provided"}
																			</span>
																			<span className="text-secondary small">
																				{item?.passport ?? "not provided"}
																			</span>
																		</div>
																	</div>
																)}
																className="w-100 p-0"
															/>
														)}
													</div>
												</div>
											);
										}
										// Add work_place special handling
										if (key.field_name === "work_place") {
											return (
												<div key={key.field_name} className="col-6 d-flex align-items-center">
													<div className="col-11 mb-2">
														<label className="ps-2 mb-2" htmlFor={key.field_name}>
															{key.description}
														</label>
														<AutoComplete
															dropdown
															placeholder="Մուտքագրեք աշխատանքային վայր"
															field="title_am"
															completeMethod={searchWorkPlace}
															suggestions={filteredWorkPlaces}
															value={inputValues[key.field_name] || null}
															onChange={e => handleInputChange(key.field_name, e.value)}
															itemTemplate={item => (
																<div className="d-grid p-1" style={{width: "300px"}}>
																	<span className="text-primary">{item?.title_am}</span>
																	<span className="text-secondary small">
																		{item?.title_en || "not provided"}
																	</span>
																</div>
															)}
															className="w-100 p-0"
														/>
													</div>
												</div>
											);
										}
										return (
											<DisabledFormFields
												key={key.field_name}
												item={key}
												disabled={false}
												remove={false}
												inputValues={inputValues}
												handleInputChange={handleInputChange}
												handleRemoveInput={handleRemoveField}
											/>
										);
									})
								) : (
									<div className="col-12 text-center p-5 opacity-50">No fields selected</div>
								)}
							</div>
						</div>

						<AutoAddRemoveRows rows={rows} rowsError={rowsError} setRows={setRows} />

						<div className="col-12 mt-3">
							<DocumentTypeSelector
								selectedTypes={selectedTypes}
								setSelectedTypes={setSelectedTypes}
								cleanedFileNames={cleanedFileNames}
							/>
						</div>
					</>
				)}
			</div>
		</Dialog>
	);
}
